// Adaptive layout wrapper for the primary (tab) screens. Below the `desktop`
// breakpoint (768 px) it renders the unchanged compact UI — TabLayout with its
// bottom tab bar — so phones and the Capacitor WebView see zero change. At
// 768 px+ it renders the desktop two-pane shell: a fixed sidebar column next to
// a content pane, capped at 1280 px and centered with hairline edges (see
// web/plan.md + web/design/prototype.html).
//
// Slot API (stable — later jobs fill the slots without editing this file):
//   • `sidebar`  — desktop left column (JW2's Sidebar). Ignored in compact
//     mode, where navigation lives in the tab bar instead.
//   • `children` — the content pane on desktop (JW3/JW5 route the empty state,
//     contact detail, or call surface here); wrapped in TabLayout in compact
//     mode, exactly as before.

import { useEffect, useState, type ReactNode } from 'react';
import { TabLayout } from './TabLayout.js';

// Kept in lockstep with the `desktop` screen in tailwind.config.ts.
const DESKTOP_QUERY = '(min-width: 768px)';

function query(): MediaQueryList | null {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return null;
  return window.matchMedia(DESKTOP_QUERY);
}

/**
 * True at the desktop breakpoint and up. Defaults to `false` (compact) wherever
 * matchMedia is unavailable (jsdom without a mock, SSR) — so the safe, tested
 * mobile layout is what renders when the environment can't tell us otherwise.
 */
export function useIsDesktop(): boolean {
  const [isDesktop, setIsDesktop] = useState(() => query()?.matches ?? false);
  useEffect(() => {
    const mql = query();
    if (!mql) return undefined;
    // Re-sync in case the width changed between the initial render and here.
    setIsDesktop(mql.matches);
    const onChange = (event: MediaQueryListEvent) => setIsDesktop(event.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);
  return isDesktop;
}

export interface AppShellProps {
  /** Content pane (desktop) / TabLayout body (compact). */
  children: ReactNode;
  /** Desktop-only left column; ignored below the breakpoint. */
  sidebar?: ReactNode;
}

export function AppShell({ children, sidebar }: AppShellProps): ReactNode {
  const isDesktop = useIsDesktop();

  // Compact: the current mobile UI, byte-for-byte — no wrapper around it, so
  // the DOM at 375 px is structurally identical to before AppShell existed.
  if (!isDesktop) {
    return <TabLayout>{children}</TabLayout>;
  }

  // Desktop: centered, 1280 px-capped two-pane grid with hairline edges. The
  // sidebar carries the internal hairline (its right border); the outer edges
  // show once the window is wider than the cap.
  return (
    <div className="flex h-full justify-center overflow-hidden">
      <div
        data-testid="app-shell-grid"
        className="grid h-full w-full max-w-[1280px] grid-cols-[340px_1fr] border-x border-edge"
      >
        <aside
          data-testid="app-shell-sidebar"
          className="flex min-h-0 flex-col border-r border-edge bg-surface"
        >
          {sidebar}
        </aside>
        {/* A layout container, not a landmark — the pane content (screen
            components) provides its own <main>. */}
        <div data-testid="app-shell-pane" className="flex min-h-0 min-w-0 flex-col">
          {children}
        </div>
      </div>
    </div>
  );
}
